import React from "react";
import { Sparkles, FlaskConical, Lightbulb, Quote } from "lucide-react";
import { PROFILE } from "../lib/content";

const HL = ({ children }) => (
  <span className="text-terracotta font-semibold">{children}</span>
);

const About = () => (
  <section
    id="about"
    data-testid="section-about"
    className="relative py-16 sm:py-20 bg-alabaster overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-10">
      {/* Top row: small photo + heading + lead */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Photo column */}
        <div className="lg:col-span-3 reveal flex justify-center">
          <div className="relative w-56 sm:w-64 lg:w-full max-w-[260px]">
            <div className="absolute -inset-3 bg-alabaster-surface rounded-[32px] -z-10" aria-hidden />
            <div className="absolute -bottom-3 -right-3 w-16 h-16 rounded-full bg-terracotta/15 -z-10 hidden sm:block" aria-hidden />
            <div className="absolute -top-3 -left-3 w-12 h-12 rounded-full border-2 border-emerald2/30 -z-10 hidden sm:block" aria-hidden />
            <img
              data-testid="about-photo"
              src={PROFILE.candidStage}
              alt="Yazid Al-Badarin"
              loading="lazy"
              className="w-full aspect-square rounded-[24px] object-cover"
              style={{ objectPosition: "center top", transform: "scaleX(-1)" }}
            />
          </div>
        </div>

        {/* Heading + lead */}
        <div className="lg:col-span-9 reveal">
          <div className="eyebrow inline-flex items-center gap-3">
            <span className="block w-10 h-px bg-terracotta" />
            About Me
          </div>
          <h2
            data-testid="about-heading"
            className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-[1.08] tracking-tight"
          >
            Hi, I'm Yazid — <span className="text-terracotta">turning ideas</span> into impact.
          </h2>
          <p
            data-testid="about-paragraph-0"
            className="mt-5 text-base sm:text-lg text-ink-soft leading-relaxed max-w-3xl"
          >
            I'm a <HL>Computer Science educator, researcher and AI innovator</HL> passionate about leveraging <HL>emerging technologies</HL> to improve education, creativity and real-world problem solving. I currently serve as the <HL>Head of Computer Science at Al-Quds Bard College</HL> and I'm a <HL>PhD candidate in Artificial Intelligence at the University of Eastern Finland</HL>.
          </p>
        </div>
      </div>

      {/* 4-card row */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        <div
          data-testid="about-paragraph-1"
          className="bg-white border border-alabaster-line rounded-2xl p-5"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-terracotta/15 text-terracotta inline-flex items-center justify-center">
              <FlaskConical size={16} strokeWidth={1.6} />
            </span>
            <div className="eyebrow !text-ink-soft !text-[10px]">My Research</div>
          </div>
          <p className="text-[13px] text-ink-soft leading-relaxed">
            Intersection of <HL>Generative AI</HL>, programming education and human-centred design. Exploring how <HL>large language models</HL> support students in <HL>debugging</HL>, problem-solving and algorithmic thinking.
          </p>
        </div>

        <div
          data-testid="about-paragraph-2"
          className="bg-white border border-alabaster-line rounded-2xl p-5"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-terracotta/15 text-terracotta inline-flex items-center justify-center">
              <Sparkles size={16} strokeWidth={1.6} />
            </span>
            <div className="eyebrow !text-ink-soft !text-[10px]">Beyond Academia</div>
          </div>
          <p className="text-[13px] text-ink-soft leading-relaxed">
            Founded <HL>multiple AI-driven educational platforms</HL>, secured <HL>patents in smart medical technologies</HL>, and lead interdisciplinary projects combining AI, healthcare and creativity.
          </p>
        </div>

        <div
          data-testid="about-paragraph-3"
          className="bg-ink text-alabaster rounded-2xl p-5"
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-lg bg-terracotta text-ink inline-flex items-center justify-center">
              <Lightbulb size={16} strokeWidth={1.8} />
            </span>
            <div className="eyebrow !text-terracotta-soft !text-[10px]">What I Teach</div>
          </div>
          <p className="text-[13px] text-alabaster/85 leading-relaxed">
            Interactive workshops on <span className="text-terracotta-soft font-semibold">AI</span>, <span className="text-terracotta-soft font-semibold">Vibe Coding</span>, <span className="text-terracotta-soft font-semibold">creative thinking</span>, <span className="text-terracotta-soft font-semibold">emerging tech</span> and <span className="text-terracotta-soft font-semibold">innovation</span>.
          </p>
        </div>

        <blockquote
          data-testid="about-paragraph-4"
          className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-5 flex flex-col"
        >
          <Quote className="text-terracotta" size={22} />
          <p className="mt-2 font-display text-[15px] text-ink italic leading-snug">
            Technology should <HL>empower people</HL>, expand opportunities, and inspire the next generation of creators.
          </p>
          <footer className="mt-3 font-display text-[11px] tracking-[0.2em] uppercase text-terracotta font-bold">
            — Yazid M. Al-Badarin
          </footer>
        </blockquote>
      </div>
    </div>
  </section>
);

export default About;
